import { useState, useRef, useEffect, useMemo } from "react";
import { Button, Icon, Input } from "@elaniintc/goes-design-system";
import { useFieldArray, useFormContext } from "react-hook-form";
import type { FormGroupSerialized, SimpleNodeSerialized } from ".";

interface variableKeys {
  type?: string;
  value?: string;
  name?: string;
  node_id?: string;
  field_id?: string;
}

const keyIcon = (type: string | undefined) => {
  if (type === "text") return ["text", "str"];
  if (type === "array") return ["list", "array"];
  if (type === "date") return ["calendar", "date"];
  if (type === "boolean") return ["toggle-large-on", "bool"];
  if (type === "number") return ["hashtag", "num"];
  if (type === "file") return ["file", "file"];
  return ["text", "str"];
};

export const FormVariableKeyBox = ({
  type = "text",
  name = "variable-key",
  field_id,
}: variableKeys) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string | undefined>("auto");

  const { control, register } = useFormContext<{
    data: FormGroupSerialized[];
  }>();
  const { fields } = useFieldArray({ control, name: "data" });

  const { fieldIndex, formIndex } = useMemo(() => {
    // get index of id and of field
    const formIndex = fields.findIndex((f) =>
      f.fields.some((f) => f.field.id === field_id),
    );
    if (formIndex === -1) return {};
    const fieldIndex = fields[formIndex].fields.findIndex(
      (f) => f.field.id === field_id,
    );
    return { fieldIndex, formIndex };
  }, [field_id, fields]);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setHeight("0px");
    }
  }, [isOpen]);

  if (fieldIndex === -1 || formIndex === -1) {
    return null;
  }

  return (
    <div className="rounded-lg border border-neutral-silent transition-all duration-300">
      <div className="py-0.5 px-3 bg-neutral-object-accent-silent flex items-center space-x-2">
        <Icon name={keyIcon(type)[0]} type="far" className="text-body" />
        <span className="text-body-loud">{name}</span>
        <span className="text-body-quiet">{keyIcon(type)[1]}</span>
        <Button
          aria-label="Toggle"
          size="icon"
          variant="ghost"
          onClick={() => setIsOpen((prev) => !prev)}
          className="ml-auto transition-transform"
        >
          <Icon
            name="edit"
            type="far"
            className={"text-body-quiet transition-transform duration-300"}
          />
        </Button>
      </div>
      <div
        ref={contentRef}
        style={{ maxHeight: height }}
        className="transition-all duration-300 overflow-hidden"
      >
        <Input
          variant="sm"
          className="w-full rounded-t-none"
          {...register(`data.${formIndex!}.fields.${fieldIndex!}.value`)}
        />
      </div>
    </div>
  );
};

export const GeneralVariableKeyBox = ({
  type = "text",
  name = "variable-key",
  node_id,
}: variableKeys) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string | undefined>("auto");

  const { control, register } = useFormContext<{
    simple_nodes: SimpleNodeSerialized[];
  }>();
  const { fields } = useFieldArray({ control, name: "simple_nodes" });

  const index = useMemo(() => {
    return fields.findIndex((f) => f.node_id === node_id);
  }, [node_id, fields]);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setHeight("0px");
    }
  }, [isOpen]);

  return (
    <div className="rounded-lg border border-neutral-silent transition-all duration-300">
      <div className="py-0.5 px-3 bg-neutral-object-accent-silent flex items-center space-x-2">
        <Icon name={keyIcon(type)[0]} type="far" className="text-body" />
        <span className="text-body-loud">{name}</span>
        <span className="text-body-quiet">{keyIcon(type)[1]}</span>
        <Button
          aria-label="Toggle"
          size="icon"
          variant="ghost"
          onClick={() => setIsOpen((prev) => !prev)}
          className="ml-auto transition-transform"
        >
          <Icon
            name="edit"
            type="far"
            className={"text-body-quiet transition-transform duration-300"}
          />
        </Button>
      </div>
      <div
        ref={contentRef}
        style={{ maxHeight: height }}
        className="transition-all duration-300 overflow-hidden"
      >
        <Input
          variant="sm"
          className="w-full rounded-t-none"
          {...register(`simple_nodes.${index}.value`)}
        />
      </div>
    </div>
  );
};
